import type { Unit } from '../models/Corpus';

export async function parseHtmlCorpus(html: string): Promise<Unit[]> {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Clean up Gutenberg cruft if present
    const body = doc.body;
    if (!body) return [];

    const units: Unit[] = [];
    let currentUnit: Partial<Unit> | null = null;
    let currentBuffer: string[] = [];
    let unitCount = 0;

    // Helper to commit current unit
    const commitUnit = () => {
        if (currentUnit && currentUnit.title) {
            const cleanText = currentBuffer.join('\n\n').trim();
            if (cleanText.length > 0) {
                // Generate content map (paragraphs)
                const paragraphs = cleanText.split(/\n\s*\n/).filter(p => p.trim().length > 0);
                const contentMap: Record<string, string> = {};
                paragraphs.forEach((p, idx) => {
                    contentMap[(idx + 1).toString()] = p.trim();
                });

                units.push({
                    id: currentUnit.id || `unit_${unitCount}`,
                    title: currentUnit.title,
                    order: unitCount,
                    text: cleanText,
                    loc: currentUnit.title, // Simple location
                    content: contentMap
                });
                unitCount++;
            }
        }
        currentBuffer = [];
        currentUnit = null;
    };

    const children = Array.from(body.children);

    children.forEach((el) => {
        const tag = el.tagName.toLowerCase();

        // Header detection
        if (['h1', 'h2', 'h3'].includes(tag)) {
            // New Unit
            commitUnit();

            const title = el.textContent?.trim() || `Chapter ${unitCount + 1}`;
            // Try to find an anchor for stable ID
            const anchor = el.querySelector('a[name]') || el.previousElementSibling?.querySelector('a[name]') || (el.previousElementSibling?.tagName === 'A' ? el.previousElementSibling : null);
            const id = anchor && (anchor as HTMLAnchorElement).name ? (anchor as HTMLAnchorElement).name : `ch_${unitCount + 1}`;

            currentUnit = {
                id,
                title
            };
        }
        else if (['p', 'div', 'blockquote'].includes(tag)) {
            if (!currentUnit) {
                const text = el.textContent || '';
                if (text.includes('START OF THE PROJECT GUTENBERG EBOOK')) {
                    return;
                }
                if (text.includes('END OF THE PROJECT GUTENBERG EBOOK')) {
                    return;
                }

                if (units.length === 0) {
                    currentUnit = { id: 'preamble', title: 'Front Matter' };
                }
            }

            if (currentUnit) {
                const t = el.textContent?.trim();
                if (t) currentBuffer.push(t);
            }
        }
    });

    commitUnit(); // Commit last

    // Fallback: if no units found (no headings), chunk by length?
    if (units.length === 0) {
        const allText = body.textContent || '';
        if (allText.trim().length > 0) {
            const contentMap: Record<string, string> = {};
            const paragraphs = allText.split(/\n\s*\n/).filter(p => p.trim().length > 0);
            paragraphs.forEach((p, idx) => {
                contentMap[(idx + 1).toString()] = p.trim();
            });

            units.push({
                id: `chapter_1`,
                title: `Chapter 1`,
                order: 0,
                text: allText,
                loc: `Chapter 1`,
                content: contentMap
            });
        }
    }

    return units;
}
